import JSZip from "jszip";

/** ZIP lớn: giải nén toàn bộ trong RAM + blob URL — giới hạn để tránh treo tab */
export const MAX_ZIP_BYTES_FOR_INLINE_PLAYER = 500 * 1024 * 1024;

function mimeFromPath(p) {
  const ext = (p.split(".").pop() || "").toLowerCase();
  const map = {
    html: "text/html",
    htm: "text/html",
    js: "application/javascript",
    mjs: "application/javascript",
    css: "text/css",
    json: "application/json",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
    mp4: "video/mp4",
    webm: "video/webm",
    mp3: "audio/mpeg",
    wav: "audio/wav",
    woff: "font/woff",
    woff2: "font/woff2",
    ttf: "font/ttf",
    otf: "font/otf",
    wasm: "application/wasm",
    xml: "application/xml",
    swf: "application/x-shockwave-flash",
  };
  return map[ext] || "application/octet-stream";
}

function normPath(p) {
  return String(p || "")
    .replace(/\\/g, "/")
    .replace(/^(\.\/)+/, "");
}

/**
 * @param {string} baseDir thư mục chứa file HTML/CSS (vd. "lesson/res/")
 * @param {string} ref đường dẫn trong thẻ src/href hoặc url()
 * @returns {string|null} key trong zip (không có leading /)
 */
function resolveZipPath(baseDir, ref) {
  const trimmed = String(ref || "").trim();
  if (!trimmed) return null;
  if (/^https?:/i.test(trimmed) || /^data:/i.test(trimmed) || trimmed.startsWith("blob:")) return null;
  if (trimmed.startsWith("#") || trimmed.startsWith("mailto:")) return null;
  const pathOnly = trimmed.split(/[?#]/)[0];
  let decoded = pathOnly;
  try {
    decoded = decodeURIComponent(pathOnly);
  } catch {
    /* ignore */
  }
  const r = normPath(decoded.replace(/^\//, ""));
  const base = normPath(baseDir);
  const parts = [...base.split("/").filter(Boolean), ...r.split("/")];
  const out = [];
  for (const seg of parts) {
    if (seg === "..") out.pop();
    else if (seg !== "." && seg !== "") out.push(seg);
  }
  return out.join("/");
}

function rewriteAttrUrl(baseDir, url, blobUrlByPath) {
  const resolved = resolveZipPath(baseDir, url);
  if (!resolved) return null;
  const blobUrl = blobUrlByPath.get(resolved.toLowerCase());
  return blobUrl || null;
}

/**
 * Trang blob: không resolve được đường dẫn con — phải thay url() trong CSS bằng blob URL thật.
 */
function rewriteCssText(cssDir, text, blobUrlByPath) {
  let out = text;
  out = out.replace(/url\((["']?)([^"')]+)\1\)/gi, (full, q, url) => {
    const inner = String(url || "").trim();
    const b = rewriteAttrUrl(cssDir, inner, blobUrlByPath);
    if (!b) return full;
    return `url(${q}${b}${q})`;
  });
  out = out.replace(/@import\s+(?:url\s*)?\(\s*(["'])([^"']+)\1\s*\)/gi, (full, q, url) => {
    const inner = String(url || "").trim();
    const b = rewriteAttrUrl(cssDir, inner, blobUrlByPath);
    if (!b) return full;
    return `@import ${q}${b}${q}`;
  });
  return out;
}

/**
 * Phần lõi mapUrl + fetch/XHR/Worker/importScripts dùng trong Worker (không có window/DOM).
 * Lưu ý: toKey dùng /^data:/i — không nhầm thư mục data/ với data: URI.
 */
function buildWorkerRuntimeShim(blobUrlMapObj, baseDir) {
  return `(function(){
var __M=${JSON.stringify(blobUrlMapObj)};
var __B=${JSON.stringify(baseDir)};
var g=typeof globalThis!=="undefined"?globalThis:typeof self!=="undefined"?self:typeof global!=="undefined"?global:window;
function norm(p){return String(p||"").replace(/\\\\/g,"/").replace(/^(\\.\\/)+/,"").replace(/^\\//,"");}
function toKey(ref){
var t=String(ref||"").trim();
if(!t||/^https?:/i.test(t)||t.indexOf("blob:")===0||/^data:/i.test(t))return null;
if(t.charAt(0)==="#"||t.indexOf("mailto:")===0)return null;
var pathOnly=t.split(/[?#]/)[0];
var dec=pathOnly;
try{dec=decodeURIComponent(pathOnly);}catch(_e){}
var r=norm(dec.replace(/^\\//,""));
var b=norm(__B);
var parts=b.split("/").filter(Boolean).concat(r.split("/"));
var out=[];
for(var i=0;i<parts.length;i++){if(parts[i]==="..")out.pop();else if(parts[i]&&parts[i]!==".")out.push(parts[i]);}
return out.join("/").toLowerCase();
}
function mapUrl(u){
if(typeof u!=="string")return u;
if(!u)return u;
if(/^https?:/i.test(u)||u.indexOf("blob:")===0||/^data:/i.test(u))return u;
var k=toKey(u);
return k&&__M[k]?__M[k]:u;
}
var of=g.fetch;
if(typeof of==="function"){
g.fetch=function(input,init){
var u=null;
if(typeof input==="string")u=input;
else if(input&&typeof input==="object"&&typeof input.url==="string")u=input.url;
if(u&&typeof u==="string"){
var m=mapUrl(u);
if(m!==u)return of.call(g,m,init);
}
return of.call(g,input,init);
};
}
var xo=XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open=function(m,url){
if(typeof url==="string"){
var m2=mapUrl(url);
if(m2!==url)arguments[1]=m2;
}
return xo.apply(this,arguments);
};
if(typeof g.importScripts==="function"){
var _is=g.importScripts;
g.importScripts=function(){
var a=[].slice.call(arguments).map(function(u){return typeof u==="string"?mapUrl(u):u;});
return _is.apply(this,a);
};
}
if(typeof Worker!=="undefined"){
var Ow=Worker;
g.Worker=function(url,opts){
if(typeof url==="string"){var w=mapUrl(url);if(w!==url)url=w;}
return new Ow(url,opts);
};
}
})();`;
}

/**
 * Mở gói ZIP (đã giải mã) iSpring/HTML5: tìm index.html, map asset → blob URL.
 * @param {Blob} blob
 * @param {{ preOpenedWindow?: Window | null }} [options] — mở sẵn cửa sổ (about:blank) trong cùng thao tác click để tránh trình duyệt chặn popup.
 */
export async function openZipPresentationFromBlob(blob, options = {}) {
  const { preOpenedWindow = null } = options;
  const size = blob.size ?? 0;
  if (size > MAX_ZIP_BYTES_FOR_INLINE_PLAYER) {
    throw new Error(
      `Gói ZIP quá lớn (${Math.round(size / (1024 * 1024))} MB) để mở trực tiếp. Vui lòng tải về và giải nén.`
    );
  }

  const zip = await JSZip.loadAsync(blob);
  const paths = Object.keys(zip.files).filter((p) => !zip.files[p].dir);

  /**
   * Thứ tự ưu tiên entry HTML (iSpring / HTML5 thường có thư mục con chứa bài):
   * 1) .../res/index.html — player iSpring chuẩn (sau khi giải nén đúng là «tên thư mục»/res/index.html)
   * 2) index.html ở bất kỳ cấp nào
   * 3) story.html
   * 4) bất kỳ file .html
   * Không mở path trên ổ đĩa: toàn bộ chạy trong RAM → blob URL (xem openZipPresentationFromBlob).
   */
  let indexPath = paths.find((p) => /(^|\/)res\/index\.html$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => /(^|\/)res\/index\.htm$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => /(^|\/)index\.html$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => /(^|\/)index\.htm$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => /(^|\/)story\.html$/i.test(p));
  if (!indexPath) indexPath = paths.find((p) => p.toLowerCase().endsWith(".html"));

  if (!indexPath) {
    throw new Error("Không tìm thấy file HTML trong ZIP.");
  }

  const baseDir = indexPath.includes("/")
    ? indexPath.slice(0, indexPath.lastIndexOf("/") + 1)
    : "";

  const blobUrlByPath = new Map();

  await Promise.all(
    paths.map(async (path) => {
      const entry = zip.file(path);
      if (!entry) return;
      const raw = await entry.async("uint8array");
      const mime = mimeFromPath(path);
      const url = URL.createObjectURL(new Blob([raw], { type: mime }));
      blobUrlByPath.set(normPath(path).toLowerCase(), url);
    })
  );

  /** CSS: url() resolve theo URL file CSS — blob gốc không có path con → rewrite nội dung. */
  const cssPaths = paths.filter((p) => /\.css$/i.test(p));
  for (const cssPath of cssPaths) {
    const cssDir = cssPath.includes("/") ? cssPath.slice(0, cssPath.lastIndexOf("/") + 1) : "";
    const entry = zip.file(cssPath);
    if (!entry) continue;
    const text = await entry.async("string");
    const rewritten = rewriteCssText(cssDir, text, blobUrlByPath);
    if (rewritten === text) continue;
    const oldUrl = blobUrlByPath.get(normPath(cssPath).toLowerCase());
    if (oldUrl) URL.revokeObjectURL(oldUrl);
    const newUrl = URL.createObjectURL(new Blob([rewritten], { type: "text/css;charset=utf-8" }));
    blobUrlByPath.set(normPath(cssPath).toLowerCase(), newUrl);
  }

  /**
   * Worker / importScripts trong thread phụ không kế thừa shim của <script> trên trang blob.
   * Prepend shim (fetch/XHR/importScripts/Worker) vào mọi .js — vài lượt để __M khớp blob URL sau khi nội dung .js đổi.
   */
  const jsPaths = paths.filter((p) => /\.js$/i.test(p));
  const jsOriginalText = new Map();
  for (const jp of jsPaths) {
    const entry = zip.file(jp);
    if (!entry) continue;
    jsOriginalText.set(jp, await entry.async("string"));
  }

  /** Hai lượt: lượt 1 __M trỏ blob gốc; lượt 2 __M trỏ blob .js đã prepend shim (Worker/importScripts). Không dùng so sánh URL vì createObjectURL luôn tạo chuỗi mới. */
  const jsShimPasses = 2;
  for (let pass = 0; pass < jsShimPasses; pass++) {
    const blobUrlMapObj = Object.fromEntries(blobUrlByPath);
    const workerShim = buildWorkerRuntimeShim(blobUrlMapObj, baseDir);
    for (const jsPath of jsPaths) {
      const orig = jsOriginalText.get(jsPath);
      if (orig == null) continue;
      const combined = `${workerShim}\n${orig}`;
      const key = normPath(jsPath).toLowerCase();
      const oldU = blobUrlByPath.get(key);
      if (oldU) URL.revokeObjectURL(oldU);
      const u = URL.createObjectURL(new Blob([combined], { type: "application/javascript" }));
      blobUrlByPath.set(key, u);
    }
  }

  let html = await zip.file(indexPath).async("string");

  const replaceSrcHref = (full, attr, q, url) => {
    const b = rewriteAttrUrl(baseDir, url, blobUrlByPath);
    if (!b) return full;
    return `${attr}=${q}${b}${q}`;
  };

  html = html.replace(/\b(src|href|data-src)\s*=\s*(["'])([^"']*)\2/gi, replaceSrcHref);

  /** HTML minify đôi khi không có dấu ngoặc quanh giá trị */
  html = html.replace(/\b(src|href|data-src)\s*=\s*([^\s"'=<>`]+)/gi, (full, attr, url) => {
    if (/^["']/.test(url)) return full;
    if (url.indexOf("blob:") === 0) return full;
    const b = rewriteAttrUrl(baseDir, url, blobUrlByPath);
    if (!b) return full;
    return `${attr}=${b}`;
  });

  html = html.replace(/url\((["']?)([^"')]+)\1\)/gi, (full, q, url) => {
    const inner = String(url || "").trim();
    const b = rewriteAttrUrl(baseDir, inner, blobUrlByPath);
    if (!b) return full;
    return `url(${q}${b}${q})`;
  });

  /**
   * Trang chính: fetch/XHR/Worker/DOM — tab blob không resolve đường dẫn tương đối.
   */
  const blobUrlMapObj = Object.fromEntries(blobUrlByPath);
  const shim = `<script>(function(){
var __M=${JSON.stringify(blobUrlMapObj)};
var __B=${JSON.stringify(baseDir)};
function norm(p){return String(p||"").replace(/\\\\/g,"/").replace(/^(\\.\\/)+/,"").replace(/^\\//,"");}
function toKey(ref){
var t=String(ref||"").trim();
if(!t||/^https?:/i.test(t)||t.indexOf("blob:")===0||/^data:/i.test(t))return null;
if(t.charAt(0)==="#"||t.indexOf("mailto:")===0)return null;
var pathOnly=t.split(/[?#]/)[0];
var dec=pathOnly;
try{dec=decodeURIComponent(pathOnly);}catch(_e){}
var r=norm(dec.replace(/^\\//,""));
var b=norm(__B);
var parts=b.split("/").filter(Boolean).concat(r.split("/"));
var out=[];
for(var i=0;i<parts.length;i++){if(parts[i]==="..")out.pop();else if(parts[i]&&parts[i]!==".")out.push(parts[i]);}
return out.join("/").toLowerCase();
}
function mapUrl(u){
if(typeof u!=="string")return u;
if(!u)return u;
if(/^https?:/i.test(u)||u.indexOf("blob:")===0||/^data:/i.test(u))return u;
var k=toKey(u);
return k&&__M[k]?__M[k]:u;
}
var of=window.fetch;
window.fetch=function(input,init){
var u=null;
if(typeof input==="string")u=input;
else if(input&&typeof input==="object"&&typeof input.url==="string")u=input.url;
if(u&&typeof u==="string"){
var m=mapUrl(u);
if(m!==u)return of.call(window,m,init);
}
return of.call(window,input,init);
};
var xo=XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open=function(m,url){
if(typeof url==="string"){
var m2=mapUrl(url);
if(m2!==url)arguments[1]=m2;
}
return xo.apply(this,arguments);
};
if(typeof Worker!=="undefined"){
var Ow=Worker;
window.Worker=function(url,opts){
if(typeof url==="string"){var w=mapUrl(url);if(w!==url)url=w;}
return new Ow(url,opts);
};
}
var sa=Element.prototype.setAttribute;
Element.prototype.setAttribute=function(name,value){
if(value!=null&&(name==="src"||name==="href"||name==="data-src"||name==="poster")){
var v=mapUrl(String(value));
if(v!==String(value))value=v;
}
return sa.call(this,name,value);
};
function patchProto(proto,prop){
try{
var d=Object.getOwnPropertyDescriptor(proto,prop);
if(!d||!d.set)return;
Object.defineProperty(proto,prop,{set:function(v){var s=String(v);var m=mapUrl(s);d.set.call(this,m!==s?m:s);},get:d.get,configurable:true});
}catch(_e){}
}
patchProto(HTMLScriptElement.prototype,"src");
patchProto(HTMLLinkElement.prototype,"href");
patchProto(HTMLImageElement.prototype,"src");
patchProto(HTMLIFrameElement.prototype,"src");
if(typeof HTMLMediaElement!=="undefined")patchProto(HTMLMediaElement.prototype,"src");
if(typeof HTMLSourceElement!=="undefined")patchProto(HTMLSourceElement.prototype,"src");
})();<\/script>`;

  if (/<head[^>]*>/i.test(html)) {
    html = html.replace(/<head[^>]*>/i, (m) => m + shim);
  } else {
    html = shim + html;
  }

  const htmlBlob = new Blob([html], { type: "text/html;charset=utf-8" });
  const htmlUrl = URL.createObjectURL(htmlBlob);
  if (preOpenedWindow && !preOpenedWindow.closed) {
    preOpenedWindow.location.replace(htmlUrl);
  } else {
    const win = window.open(htmlUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      URL.revokeObjectURL(htmlUrl);
      for (const u of blobUrlByPath.values()) URL.revokeObjectURL(u);
      throw new Error("Trình duyệt đã chặn cửa sổ mới. Vui lòng cho phép popup.");
    }
  }
  window.setTimeout(() => URL.revokeObjectURL(htmlUrl), 60_000);
}
